/**
 * Darts-Cricket's presentation, free of React Native so node tests, the TV
 * page and the Apple TV share it (DARTS_CRICKET.md "On the green" and "HUD"):
 * the board on the green, the standing labels, the helper table, the result
 * popup and turn summary, the corner, the scoreboard lines, and the TV game
 * state the phone publishes.
 */
import type { PuttSceneRolloutBand } from '../../../../packages/putt-scene/src/SceneContent';
import { DART_LABEL_GAP_FT, dartBoardEdgeFt, type PuttSceneDartBoard } from '../../../../packages/putt-scene/src/dartBoard';
import {
  CRICKET_BANDS,
  CRICKET_BULL,
  CRICKET_BULL_CENTER_FT,
  CRICKET_BULL_INNER_RADIUS_FT,
  CRICKET_BULL_OUTER_RADIUS_FT,
  CRICKET_DARTS_PER_TURN,
  CRICKET_DOUBLE_DEG,
  CRICKET_TARGETS,
  CRICKET_TRIPLE_DEG,
  cricketIsClosed,
  cricketPlayerIn,
  cricketTargetIndex,
  type CricketClinch,
  type CricketDart,
  type CricketHit,
  type CricketState,
  type CricketTarget,
} from './cricket';
import { ADULT_DISTANCES, type DistanceView } from './kidDistances';

/** Ball and name colors by seat: white, red, blue, gold. */
export const CRICKET_SEAT_COLORS = ['#FFFFFF', '#F0605D', '#4FC3F7', '#E8C46B'] as const;

/** How long the result popup stays up (Cole's lesson from Football: 2 s). */
export const CRICKET_POPUP_MS = 2000;
/** The popup after a turn's last putt also carries the turn summary, a beat longer. */
export const CRICKET_TURN_POPUP_MS = 2600;

const LABEL_WHITE = '#FFFFFF';
export const CRICKET_LABEL_SCALE = 1.1;

/** The feet mark after a distance (Cole, 2026-10-20: "put ' marks after the
 *  distances to imply feet"): the prime, which the 3D label font draws. */
export const CRICKET_FEET_MARK = String.fromCharCode(0x2032);

/** The table and the green's name for a target. */
export function cricketTargetLabel(target: CricketTarget): string {
  return target === CRICKET_BULL ? 'BULL' : String(target);
}

/**
 * A band's (or the bull's) distance span down the line. `view`: the putting
 * seat's field; a Rookie or Pro turn reads its level's board (kidModes.ts CRICKET_LEVEL_MAPS).
 */
export function cricketTargetSpanFt(target: CricketTarget, view: DistanceView = ADULT_DISTANCES): { minFt: number; maxFt: number } {
  if (target === CRICKET_BULL) {
    const center = view.ft(CRICKET_BULL_CENTER_FT);
    const radius = view.tolFt(CRICKET_BULL_OUTER_RADIUS_FT);
    return { minFt: center - radius, maxFt: center + radius };
  }
  const band = CRICKET_BANDS.find((entry) => entry.target === target)!;
  return { minFt: view.ft(band.minFt), maxFt: view.ft(band.maxFt) };
}

/** The first distance of a target (the right rail): 20, 25 ... 45, and 55 for the bull. */
export function cricketTargetFirstFt(target: CricketTarget, view: DistanceView = ADULT_DISTANCES): number {
  return target === CRICKET_BULL ? view.ft(CRICKET_BULL_CENTER_FT) : cricketTargetSpanFt(target, view).minFt;
}

/** The table's distance column, in feet: "20–23′", the bull "55′". */
export function cricketTargetDistanceText(target: CricketTarget, view: DistanceView = ADULT_DISTANCES): string {
  if (target === CRICKET_BULL) return `${view.ft(CRICKET_BULL_CENTER_FT)}${CRICKET_FEET_MARK}`;
  const { minFt, maxFt } = cricketTargetSpanFt(target, view);
  return `${minFt}–${maxFt}${CRICKET_FEET_MARK}`;
}

/** The fans and the bull for the scene. A closed number is painted out of play. */
export function cricketDartBoard(state: CricketState | null, view: DistanceView = ADULT_DISTANCES): PuttSceneDartBoard {
  return {
    tripleDeg: CRICKET_TRIPLE_DEG,
    doubleDeg: CRICKET_DOUBLE_DEG,
    // Every band in the same colors (green triple, yellow doubles, red singles).
    bands: CRICKET_BANDS.map((band) => ({
      id: String(band.target),
      minFt: view.ft(band.minFt),
      maxFt: view.ft(band.maxFt),
      ...(state && cricketIsClosed(state, band.target) ? { closed: true } : {}),
    })),
    // The wedges are angles, so a Rookie or Pro board keeps them; only distances move in.
    bull: {
      centerFt: view.ft(CRICKET_BULL_CENTER_FT),
      outerRadiusFt: view.tolFt(CRICKET_BULL_OUTER_RADIUS_FT),
      innerRadiusFt: view.tolFt(CRICKET_BULL_INNER_RADIUS_FT),
      ...(state && cricketIsClosed(state, CRICKET_BULL) ? { closed: true } : {}),
    },
  };
}

/**
 * The labels (Cole's notes 2026-10-20): the number's name on the
 * LEFT and its first distance on the RIGHT, white, painted flat on the turf
 * (2026-10-21, so they read as the ball rolls past from the normal putting
 * view) at
 * exactly the band's middle distance (the bull's: 55 ft), each with its inner
 * edge just outside the drawn board at that distance, so it reads as part of
 * the fan. Label-only bands: no cross-lines (the fans mark the bands), one
 * side each, never alternating. A closed number's labels are hidden; its fan
 * is painted out of play by the board.
 */
export function cricketRolloutBands(state: CricketState | null, view: DistanceView = ADULT_DISTANCES): PuttSceneRolloutBand[] {
  return CRICKET_TARGETS.flatMap((target) => {
    const span = cricketTargetSpanFt(target, view);
    const closed = Boolean(state && cricketIsClosed(state, target));
    const railFt = cricketLabelRailFt(target, view);
    const base = {
      minFt: span.minFt,
      maxFt: span.maxFt,
      labelOrientation: 'flat' as const,
      labelOnly: true,
      labelColor: LABEL_WHITE,
      lineColor: LABEL_WHITE,
      labelScale: CRICKET_LABEL_SCALE,
      labelRailFt: railFt,
      ...(closed ? { hidden: true } : {}),
    };
    return [
      { ...base, id: `cricket-${cricketTargetLabel(target)}`, label: cricketTargetLabel(target), labelSide: 'left' as const },
      { ...base, id: `cricket-${cricketTargetLabel(target)}-ft`, label: `${cricketTargetFirstFt(target, view)}${CRICKET_FEET_MARK}`, labelSide: 'right' as const },
    ];
  });
}

/** Where a target's labels stand (ft from the line): just outside the drawn board at its middle distance. */
export function cricketLabelRailFt(target: CricketTarget, view: DistanceView = ADULT_DISTANCES): number {
  if (target === CRICKET_BULL) return view.tolFt(CRICKET_BULL_OUTER_RADIUS_FT) + DART_LABEL_GAP_FT;
  const span = cricketTargetSpanFt(target, view);
  return Math.round((dartBoardEdgeFt((span.minFt + span.maxFt) / 2) + DART_LABEL_GAP_FT) * 100) / 100;
}

export interface CricketTableRow {
  key: string;
  label: string;
  distance: string;
  /** Per player, 0 to 3. */
  marks: number[];
  /** Closed for everyone: crossed out. */
  closed: boolean;
}

/** The helper table, bottom left: one row per target in board order. */
export function cricketTableRows(state: CricketState, view: DistanceView = ADULT_DISTANCES): CricketTableRow[] {
  return CRICKET_TARGETS.map((target) => {
    const index = cricketTargetIndex(target);
    return {
      key: cricketTargetLabel(target),
      label: cricketTargetLabel(target),
      distance: cricketTargetDistanceText(target, view),
      marks: state.marks.map((row) => Math.min(3, row[index] ?? 0)),
      closed: cricketIsClosed(state, target),
    };
  });
}

/**
 * Darts scoreboard marks: one slash, an X, then a circled X once the number
 * is open. Renderers draw 3 as an X inside a ring; this text form is the
 * fallback and the accessibility label.
 */
export function cricketMarkGlyph(count: number): string {
  if (count >= 3) return 'Ⓧ';
  if (count === 2) return 'X';
  if (count === 1) return '/';
  return '';
}

export function cricketMarkSpoken(count: number): string {
  if (count >= 3) return 'open';
  if (count === 2) return 'two marks';
  if (count === 1) return 'one mark';
  return 'no marks';
}

/** "TRIPLE 18!", "DOUBLE BULL!", "BULL!", "SINGLE 15", "MISS". */
export function cricketHitLabel(hit: CricketHit): string {
  if (hit.kind === 'miss') return 'MISS';
  if (hit.kind === 'bull') return hit.marks === 2 ? 'DOUBLE BULL!' : 'BULL!';
  if (hit.multiplier === 3) return `TRIPLE ${hit.target}!`;
  if (hit.multiplier === 2) return `DOUBLE ${hit.target}!`;
  return `SINGLE ${hit.target}`;
}

export type CricketTone = 'gold' | 'danger' | 'light';

export interface CricketPopup {
  label: string;
  /** Under the label: points, opened, closed, or the turn summary. */
  detail: string | null;
  tone: CricketTone;
  key: number;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/** "THIS TURN: 5 MARKS · 54 PTS" ("NO MARKS" for an empty turn). */
export function cricketTurnSummary(turn: { marks: number; points: number } | null): string | null {
  if (!turn) return null;
  const marks = turn.marks > 0 ? plural(turn.marks, 'MARK', 'MARKS') : 'NO MARKS';
  return turn.points > 0 ? `THIS TURN: ${marks} · ${turn.points} PTS` : `THIS TURN: ${marks}`;
}

/** What a dart did, under its label: "+36 PTS", "18 OPEN", "18 CLOSED". A dart on a closed number has none. */
export function cricketDartDetail(dart: CricketDart): string | null {
  if (dart.hit.kind === 'miss') return null;
  const name = dart.hit.kind === 'bull' ? 'BULL' : String(dart.hit.target);
  if (dart.dead) return null;
  const parts: string[] = [];
  if (dart.closedOut) parts.push(`${name} CLOSED`);
  else if (dart.opened) parts.push(`${name} OPEN`);
  if (dart.points > 0) parts.push(`+${dart.points} PTS`);
  return parts.length ? parts.join(' · ') : null;
}

/**
 * The popup for a putt. On the turn's last putt (or the game's) it also
 * carries the turn summary.
 */
export function cricketDartPopup(dart: CricketDart, turn: { marks: number; points: number } | null, key: number): CricketPopup {
  const tone: CricketTone = dart.hit.kind === 'miss' || dart.dead
    ? 'danger'
    : (dart.hit.kind === 'number' && dart.hit.multiplier === 3) || (dart.hit.kind === 'bull' && dart.hit.marks === 2)
      ? 'gold'
      : 'light';
  const detail = cricketDartDetail(dart);
  const summary = cricketTurnSummary(turn);
  // A dart on a number everyone has closed is a miss, and says only that
  // (Cole, 2026-10-29: "it said double 15 closed. It should just say miss").
  const label = dart.dead ? 'MISS' : cricketHitLabel(dart.hit);
  return { label, detail: summary ? [detail, summary].filter(Boolean).join('\n') : detail, tone, key };
}

/** "ROUND 4 OF 15"; null for No limit (Cole: show nothing then). */
export function cricketRoundLabel(state: CricketState): string | null {
  return state.rounds === null ? null : `ROUND ${Math.min(state.round, state.rounds)} OF ${state.rounds}`;
}

/** The big corner text: "DART 2 OF 3". */
export function cricketDartLabel(state: CricketState): string | null {
  if (state.phase === 'complete') return null;
  return `DART ${Math.min(CRICKET_DARTS_PER_TURN, state.dart + 1)} OF ${CRICKET_DARTS_PER_TURN}`;
}

/** The winner line: "COLE WINS", "DRAW". */
export function cricketResultLine(state: CricketState, names: readonly string[]): string {
  if (state.draw) return 'DRAW';
  if (state.winner === null) return '';
  return `${(names[state.winner] ?? '').toUpperCase()} WINS`;
}

/** Why it ended, under the winner on the final card. */
export function cricketEndedLine(state: CricketState): string {
  if (state.endedBy === 'closed') return state.draw ? 'EVERY NUMBER CLOSED' : 'CLOSED THEM ALL';
  if (state.endedBy === 'rounds') return state.draw ? 'TIED ON POINTS AND MARKS' : 'MOST POINTS AFTER THE LAST ROUND';
  if (state.endedBy === 'clinched') return 'CLINCHED';
  return '';
}

/** The clinch card: "COLE HAS CLINCHED IT" / "STU NEEDS 61, BEST POSSIBLE IS 60". */
export function cricketClinchText(clinch: CricketClinch, names: readonly string[]): { title: string; detail: string } {
  const leader = (names[clinch.leader] ?? '').toUpperCase();
  const trailer = (names[clinch.closest.player] ?? '').toUpperCase();
  const detail = clinch.trailers.length > 1
    ? `NOBODY CAN CATCH UP · ${trailer} NEEDS ${clinch.closest.needs}, BEST POSSIBLE IS ${clinch.closest.bestPossible}`
    : `${trailer} NEEDS ${clinch.closest.needs}, BEST POSSIBLE IS ${clinch.closest.bestPossible}`;
  return { title: `${leader} HAS CLINCHED IT`, detail };
}

/**
 * The scoreboard's status line: whose putt it is, and, at the start of a turn, what the last
 * turn did ("COLE TO PUTT · SANDY LAST TURN: 4 MARKS"). Cole, 2026-10-25: it read "SANDY · THIS
 * TURN: 4 MARKS" while it was his turn. A player who has stopped is not summarized.
 */
export function cricketStatusLine(state: CricketState, names: readonly string[]): string {
  if (state.phase === 'complete') return cricketResultLine(state, names);
  const up = `${(names[state.turn] ?? '').toUpperCase()} TO PUTT`;
  const last = state.dart === 0 ? state.lastTurn : null;
  const summary = last && cricketPlayerIn(state, last.player) ? cricketTurnSummary(last) : null;
  if (!summary || !last) return up;
  return `${up} · ${(names[last.player] ?? '').toUpperCase()} ${summary.replace('THIS TURN', 'LAST TURN')}`;
}

export type CricketHostPhase = 'setup' | 'handoff' | 'putt' | 'resolving' | 'result';

/** The TV's game state (web/tv/src/cricketBoard.ts reads it). JSON-safe, no nested arrays. */
export function cricketTvGameState(
  state: CricketState,
  options: {
    names: readonly string[];
    colors?: readonly string[];
    hostPhase: CricketHostPhase;
    popup: CricketPopup | null;
    phonePrompt?: { player: string; action: string; color?: string } | null;
    /** The game the HUD should show (the popup's putt, before the state moves on). */
    display?: CricketState;
    /** The putting seat's field: a Rookie or Pro turn lists its level's board (kidDistances.ts). */
    view?: DistanceView;
  },
): Record<string, unknown> {
  const shown = options.display ?? state;
  return {
    kind: 'cricket',
    names: [...options.names],
    colors: [...(options.colors ?? CRICKET_SEAT_COLORS.slice(0, state.players))],
    phase: options.hostPhase,
    points: [...shown.points],
    turn: shown.turn,
    dart: shown.dart,
    round: shown.round,
    rounds: shown.rounds,
    roundLabel: cricketRoundLabel(shown),
    dartLabel: cricketDartLabel(shown),
    table: cricketTableRows(shown, options.view),
    status: cricketStatusLine(shown, options.names),
    turnSummary: cricketTurnSummary(shown.lastTurn),
    popup: options.popup,
    winner: state.phase === 'complete' ? state.winner : null,
    draw: state.phase === 'complete' && state.draw,
    phonePrompt: options.phonePrompt ?? null,
    // Players who stopped (live play): the TV crosses them out (Cole, 2026-10-25).
    out: Array.from({ length: state.players }, (_, player) => !cricketPlayerIn(shown, player)),
  };
}
